import { ExternalActionFlow } from './ExternalActionFlow';
import { SessionProvider } from 'core/infrastructure';
import { Res } from 'core/api';
import { Message as MessageModal } from 'core/ui-next/modal';

const MESSAGE_TYPES = {
	CloseWindow: 'agora:pos:close-window',
	InvokeApi: 'agora:pos:invoke-api'
} as const;


function isValidType(type: string): boolean {
	return (Object.values(MESSAGE_TYPES) as string[]).includes(type);
}

type Message = {
	type: typeof MESSAGE_TYPES.CloseWindow,
	applyFlow: ExternalActionFlow
} | {
	type: typeof MESSAGE_TYPES.InvokeApi,
	requestId: string,
	apiToken: string,
	endpoint: string,
	body: object | string
};

// Tipo de ayuda para extraer un tipo concreto de mensaje
type EM<V extends Message['type']> = Extract<Message, { type: V }>;


export class IframeInterop {

	constructor(
		private allowedOrigin: string,
		private closeWindow: (action: ExternalActionFlow) => void,
	) {
	}

	public start(): void {
		window.addEventListener('message', this.onMessageReceived);
	}

	public stop(): void {
		window.removeEventListener('message', this.onMessageReceived);
	}

	private onMessageReceived = async (event: MessageEvent): Promise<void> => {
		// Si el mensaje no me llega desde mi origen, lo descarto y logeo en consola (para facilitar la depuración a terceros)
		if (event.origin !== this.allowedOrigin) {
			// eslint-disable-next-line no-console
			console.log(`Recibido mensaje desde origen no permitido: ${event.origin}`);
			return;
		}

		const message = event.data as Message;

		if (!isValidType(message.type))
			return;

		try {
			switch (message.type) {
				case MESSAGE_TYPES.CloseWindow: await this.onCloseWindow(message); break;
				case MESSAGE_TYPES.InvokeApi: await this.onInvokeApi(message, event.source!); break;
			}

		} catch (error: any) {
			await MessageModal.show(Res.Msg.MSG_ERROR_INVOKING_CUSTOM_ACTION(error.message ?? error.toString?.()));
		}
	};

	private async onCloseWindow(message: EM<typeof MESSAGE_TYPES.CloseWindow>): Promise<void> {
		this.closeWindow(message.applyFlow);
	}

	private async onInvokeApi(
		{ endpoint, apiToken, body, requestId }: EM<typeof MESSAGE_TYPES.InvokeApi>,
		source: MessageEventSource
	): Promise<void> {

		const { AdminHttpPort, AdminHttpUseSsl } = SessionProvider.getCurrent();
		const url = `http${AdminHttpUseSsl ? 's' : ''}://${document.location.hostname}:${AdminHttpPort}/${endpoint.replace(/^\/+/, '')}`;

		const reply = (response: any) => source.postMessage({ requestId, ...response }, { targetOrigin: this.allowedOrigin });

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-type': 'application/json; charset=utf-8',
				'Api-Token': apiToken,
				'Accept': 'application/json'
			},
			body: typeof body === 'string' ? body : JSON.stringify(body)
		});

		try {
			if (response.status !== 200)
				throw new Error(`Server Error (${response.status}): ${response.statusText}`);

			// No todas nuestras APIs devuelve JSON (somos así de incómodos de integrar), por ejemplo
			// pasa con el API de impresión.
			if ((response.headers.get('Content-Type') ?? '').includes('json'))
				reply({ response: await response.json() });
			else
				reply({ response: await response.text() });

		}
		catch (error: any) {
			reply({ error: error.message ?? 'Invocation failed' });
		}
	}
}